import { useCallback, useEffect, useRef, useState } from "react";
import { mappingApi } from "@services/mappingApi";
import { ApiError } from "@utils/httpClient";
import {
  ReviewStatus,
  type DisplayConfig,
  type RowEdit,
  type RowEditInput,
  type SearchedMatch,
} from "@models/job";
import { MappingResultKind } from "@models/mappingResult";
import type { MappingResultRow, RowAlternatives } from "@models/mappingResult";
import {
  loadEditsFromStorage,
  persistEditsToStorage,
} from "./useJobResults.storage";
import {
  toPatchPayload,
  type BulkEditEntry,
  type BulkEditResult,
  type RowSaveStatus,
  type UseJobResultsState,
} from "./useJobResults.types";

export type {
  RowSaveStatus,
  UseJobResultsState,
} from "./useJobResults.types";

// The API caps `limit` at 500. Asking for the cap trims round trips on a big
// job, but it does NOT decide how many rows come back: a DynamoDB Query stops
// at 1 MB of data read BEFORE `Limit` applies, and a result row is heavy on the
// read side (the projection has to include `alternatives` so the API can
// resolve a corrected row's chosen match). Measured on develop, that ceiling
// lands around 110-130 rows per request whatever we ask for — so this is a
// modest saving, not a way to fetch a whole job in one call. The cursor walk
// below is what actually reads everything, and it stays required.
const PAGE_SIZE = 500;
const NOT_READY_RETRY_MS = 4_000;
const BULK_CONCURRENCY = 5;

export const useJobResults = (
  jobId: string | null,
  statusVersion: number,
): UseJobResultsState => {
  const [rows, setRows] = useState<MappingResultRow[]>([]);
  const [edits, setEdits] = useState<Map<number, RowEdit>>(new Map());
  const [saveStates, setSaveStates] = useState<Map<number, RowSaveStatus>>(
    new Map(),
  );
  const [cursor, setCursor] = useState<string | null>(null);
  const [display, setDisplay] = useState<DisplayConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState(false);
  const [alternativesLoading, setAlternativesLoading] = useState<Set<number>>(
    new Set(),
  );
  // Candidates are their OWN endpoint now, so they get their own cache rather
  // than being merged onto the row. That is what retires the old "preserve the
  // alternatives when a PATCH response comes back without them" special case:
  // a server row can never clobber them because it no longer carries any.
  const [alternatives, setAlternatives] = useState<Map<number, RowAlternatives>>(
    new Map(),
  );
  const altLoadedRef = useRef<Set<number>>(new Set());
  const altLoadingRef = useRef<Set<number>>(new Set());
  const retryTimerRef = useRef<number | null>(null);
  const savedFadeTimersRef = useRef<Map<number, number>>(new Map());
  /** Generation counter for pagination chains. Each first-page fetch starts a
   * new generation; a chain whose generation is stale (a refresh, a SignalR
   * status bump, or an unmount started a newer one) drops its response
   * instead of applying it. Without this, two chains interleave and the same
   * row lands in `rows` twice — duplicate React keys corrupt the table
   * (ghost/duplicated alternatives under an expanded row). */
  const fetchGenRef = useRef(0);

  const setSaveState = useCallback(
    (rowIndex: number, status: RowSaveStatus | null): void => {
      setSaveStates((prev) => {
        const next = new Map(prev);
        if (status === null) next.delete(rowIndex);
        else next.set(rowIndex, status);
        return next;
      });
    },
    [],
  );

  const scheduleSavedFade = useCallback(
    (rowIndex: number): void => {
      const existing = savedFadeTimersRef.current.get(rowIndex);
      if (existing !== undefined) window.clearTimeout(existing);
      const handle = window.setTimeout(() => {
        savedFadeTimersRef.current.delete(rowIndex);
        setSaveState(rowIndex, null);
      }, 2_000);
      savedFadeTimersRef.current.set(rowIndex, handle);
    },
    [setSaveState],
  );

  const cancelRetry = useCallback((): void => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const fetchPage = useCallback(
    async (
      targetJobId: string,
      nextCursor: string | null,
      generation?: number,
    ): Promise<void> => {
      const isFirstPage = nextCursor === null;
      // A call without a generation starts a NEW chain and invalidates every
      // older one (their in-flight responses are dropped below).
      const gen = generation ?? ++fetchGenRef.current;
      if (isFirstPage) {
        setLoading(true);
        setError(null);
        setNotReady(false);
      } else {
        setLoadingMore(true);
      }
      try {
        const response = await mappingApi.getJobResult(targetJobId, {
          cursor: nextCursor ?? undefined,
          limit: PAGE_SIZE,
        });
        if (gen !== fetchGenRef.current) return; // stale chain — drop it
        setRows((prev) => {
          if (isFirstPage) return response.rows;
          // Belt-and-braces: never let the same row_index in twice even if a
          // page boundary shifts under us mid-walk (rows added/edited while
          // paging) — duplicate keys corrupt the table render.
          const seen = new Set(prev.map((r) => r.rowIndex));
          return [...prev, ...response.rows.filter((r) => !seen.has(r.rowIndex))];
        });
        setCursor(response.cursor);
        if (response.display !== undefined) setDisplay(response.display);
        // Show everything: keep pulling pages until the cursor is exhausted
        // so the TM review table renders the whole result set (no manual
        // "load more"). Each follow-up page accumulates onto `rows`.
        if (response.cursor) {
          void fetchPage(targetJobId, response.cursor, gen);
        }
      } catch (err) {
        if (gen !== fetchGenRef.current) return; // stale chain — stay quiet
        if (err instanceof ApiError && err.status === 409) {
          setNotReady(true);
          cancelRetry();
          retryTimerRef.current = window.setTimeout(() => {
            void fetchPage(targetJobId, null);
          }, NOT_READY_RETRY_MS);
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed to load results";
        setError(message);
      } finally {
        if (isFirstPage) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [cancelRetry],
  );

  useEffect(() => {
    cancelRetry();
    fetchGenRef.current += 1; // invalidate any chain from the previous job/version
    setRows([]);
    setCursor(null);
    setDisplay(null);
    setError(null);
    setNotReady(false);
    setSaveStates(new Map());
    setAlternativesLoading(new Set());
    altLoadedRef.current.clear();
    altLoadingRef.current.clear();
    for (const handle of savedFadeTimersRef.current.values()) {
      window.clearTimeout(handle);
    }
    savedFadeTimersRef.current.clear();
    if (!jobId) {
      setEdits(new Map());
      return;
    }
    setEdits(loadEditsFromStorage(jobId));
    void fetchPage(jobId, null);
    return () => {
      cancelRetry();
      fetchGenRef.current += 1; // stop the walking chain on unmount
    };
  }, [jobId, statusVersion, fetchPage, cancelRetry]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (!jobId || !cursor || loadingMore) return;
    await fetchPage(jobId, cursor);
  }, [jobId, cursor, loadingMore, fetchPage]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!jobId) return;
    await fetchPage(jobId, null);
  }, [jobId, fetchPage]);

  const addRow = useCallback(
    async (
      itemMpId: number,
      value: string,
      searched?: {
        itemMpText: string;
        matches: SearchedMatch[];
        degraded?: string[];
      },
    ): Promise<void> => {
      if (!jobId) return;
      const row = await mappingApi.addResultRow(jobId, {
        itemMpId,
        value,
        ...(searched
          ? {
              itemMpText: searched.itemMpText,
              matches: searched.matches,
              degraded: searched.degraded,
            }
          : {}),
      });
      setRows((prev) => [...prev, row]);
    },
    [jobId],
  );


  const persistEdit = useCallback(
    (rowIndex: number, partial: RowEditInput): void => {
      setEdits((prev) => {
        const next = new Map(prev);
        next.set(rowIndex, { ...partial, editedAt: new Date().toISOString() });
        if (jobId) persistEditsToStorage(jobId, next);
        return next;
      });
    },
    [jobId],
  );

  const mergeServerRow = useCallback(
    (rowIndex: number, serverRow: MappingResultRow): void => {
      // A straight replace: the server row is the whole row. It carries no
      // candidates to lose (they live in their own cache), and its `target` is
      // already resolved to whatever pick the edit just stored.
      setRows((prev) =>
        prev.map((row) => (row.rowIndex === rowIndex ? serverRow : row)),
      );
    },
    [],
  );

  /**
   * Lazily fetch + cache one row's candidates the first time its dropdown is
   * opened. The result list ships only `hasAlternative`, so they load on
   * demand. Cached per row index; in-flight requests are de-duplicated.
   *
   * `kind` is passed in rather than looked up here. The alternatives response
   * carries no discriminator — deliberately, since one request is one row — and
   * the caller is a per-plugin table that knows statically which kind it
   * renders. Searching `rows` for it would put the whole row list in this
   * callback's dependencies, giving it a new identity on every page append and
   * every edit, and would have to guess a kind for a row it could not find.
   */
  const loadAlternatives = useCallback(
    async (rowIndex: number, kind: MappingResultKind): Promise<void> => {
      if (!jobId) return;
      if (
        altLoadedRef.current.has(rowIndex) ||
        altLoadingRef.current.has(rowIndex)
      ) {
        return;
      }
      altLoadingRef.current.add(rowIndex);
      setAlternativesLoading((prev) => new Set(prev).add(rowIndex));
      try {
        const loaded = await mappingApi.getRowAlternatives(
          jobId,
          rowIndex,
          kind,
        );
        altLoadedRef.current.add(rowIndex);
        setAlternatives((prev) => new Map(prev).set(rowIndex, loaded));
      } catch {
        // Leave the row unloaded so the next expand retries.
      } finally {
        altLoadingRef.current.delete(rowIndex);
        setAlternativesLoading((prev) => {
          const next = new Set(prev);
          next.delete(rowIndex);
          return next;
        });
      }
    },
    [jobId],
  );

  /** Merge a single server row into local state (e.g. after a revision
   * restore) — avoids re-fetching the whole result set. */
  const applyServerRow = useCallback(
    (serverRow: MappingResultRow): void => {
      mergeServerRow(serverRow.rowIndex, serverRow);
    },
    [mergeServerRow],
  );

  const dropEdit = useCallback(
    (rowIndex: number): void => {
      setEdits((prev) => {
        if (!prev.has(rowIndex)) return prev;
        const next = new Map(prev);
        next.delete(rowIndex);
        if (jobId) persistEditsToStorage(jobId, next);
        return next;
      });
    },
    [jobId],
  );

  const submitEdit = useCallback(
    async (
      rowIndex: number,
      partial: RowEditInput,
    ): Promise<void> => {
      if (!jobId) return;
      setSaveState(rowIndex, { state: "saving" });
      try {
        const response = await mappingApi.patchResult(
          jobId,
          rowIndex,
          toPatchPayload(partial),
        );
        mergeServerRow(rowIndex, response.row);
        dropEdit(rowIndex);
        setSaveState(rowIndex, { state: "saved" });
        scheduleSavedFade(rowIndex);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Save failed";
        setSaveState(rowIndex, { state: "failed", error: message });
      }
    },
    [jobId, mergeServerRow, dropEdit, scheduleSavedFade, setSaveState],
  );

  const applyEdit = useCallback(
    (rowIndex: number, partial: RowEditInput): void => {
      persistEdit(rowIndex, partial);
      void submitEdit(rowIndex, partial);
    },
    [persistEdit, submitEdit],
  );

  const clearEdit = useCallback(
    (rowIndex: number): void => {
      dropEdit(rowIndex);
      setSaveState(rowIndex, null);
    },
    [dropEdit, setSaveState],
  );

  const retrySave = useCallback(
    (rowIndex: number): void => {
      const edit = edits.get(rowIndex);
      if (!edit) return;
      void submitEdit(rowIndex, edit);
    },
    [edits, submitEdit],
  );

  const bulkApplyEdits = useCallback(
    async (entries: BulkEditEntry[]): Promise<BulkEditResult> => {
      if (entries.length === 0) return { succeeded: 0, failed: 0 };
      if (!jobId) return { succeeded: 0, failed: entries.length };
      let succeeded = 0;
      let failed = 0;
      for (const entry of entries) persistEdit(entry.rowIndex, entry.edit);

      const queue = [...entries];
      const worker = async (): Promise<void> => {
        while (queue.length > 0) {
          const entry = queue.shift();
          if (!entry) return;
          setSaveState(entry.rowIndex, { state: "saving" });
          try {
            const response = await mappingApi.patchResult(
              jobId,
              entry.rowIndex,
              toPatchPayload(entry.edit),
            );
            mergeServerRow(entry.rowIndex, response.row);
            dropEdit(entry.rowIndex);
            setSaveState(entry.rowIndex, { state: "saved" });
            scheduleSavedFade(entry.rowIndex);
            succeeded += 1;
          } catch (err) {
            const message =
              err instanceof ApiError
                ? err.message
                : err instanceof Error
                  ? err.message
                  : "Save failed";
            setSaveState(entry.rowIndex, { state: "failed", error: message });
            failed += 1;
          }
        }
      };

      const workers = Array.from(
        { length: Math.min(BULK_CONCURRENCY, entries.length) },
        () => worker(),
      );
      await Promise.all(workers);
      return { succeeded, failed };
    },
    [
      jobId,
      persistEdit,
      mergeServerRow,
      dropEdit,
      scheduleSavedFade,
      setSaveState,
    ],
  );

  return {
    rows,
    edits,
    saveStates,
    cursor,
    display,
    loading,
    loadingMore,
    error,
    hasMore: cursor !== null,
    notReady,
    alternatives,
    alternativesLoading,
    applyEdit,
    clearEdit,
    retrySave,
    bulkApplyEdits,
    loadMore,
    loadAlternatives,
    refresh,
    addRow,
    applyServerRow,
  };
};

export const resolveReviewStatus = (
  row: MappingResultRow,
  edit: RowEdit | undefined,
): ReviewStatus => {
  if (edit) return edit.reviewStatus;
  return row.reviewStatus;
};
